import { WorkflowStep } from 'src/utils/interfaces/workflow.interfaces';
import { RotorTypeSelections, TolerancePlanePositions } from 'src/utils/enums/rotor-type.enum';
import { RotorTypeConfigurationModel } from 'src/utils/interfaces/rotor-type.interfaces';
import { BehaviorSubject } from 'rxjs';
export type LaserType = 'NoLaser' | 'Laser';
import * as IMAGE_RENDERER from 'src/utils/constants/image-renderer.const';

export const RotorTypeConfiguration: RotorTypeConfigurationModel[] = [
  {
    title: 'Static',
    value: RotorTypeSelections.Static,
    index: 6,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/Static',
    tolerancesImageSrc: './assets/img/tolerances/main/Static',
    class: 'static',
  },
  {
    title: 'Centered',
    value: RotorTypeSelections.Centered,
    index: 0,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/Centered',
    tolerancesImageSrc: './assets/img/tolerances/main/Centered',
    class: 'centered',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.MIDLEFT_Px,
        t2: IMAGE_RENDERER.MIDRIGHT_Px,
      },
    },
  },
  {
    title: 'Center + Overhung Right',
    value: RotorTypeSelections.CenterRight,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/CenterRight',
    tolerancesImageSrc: './assets/img/tolerances/main/CenterRight',
    index: 1,
    class: 'center-right',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.MIDRIGHT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.MIDLEFT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
    },
  },
  {
    title: 'Overhung Right',
    value: RotorTypeSelections.OverhungRight,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/OverhungRight',
    tolerancesImageSrc: './assets/img/tolerances/main/OverhungRight',
    index: 2,
    class: 'overhung-right',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.MIDLEFT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.MIDRIGHT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
    },
  },
  {
    title: 'Overhung Left + Overhung Right',
    value: RotorTypeSelections.Polar,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/Polar',
    tolerancesImageSrc: './assets/img/tolerances/main/Polar',
    index: 5,
    class: 'polar',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.MIDLEFT_Px,
        t2: IMAGE_RENDERER.MIDRIGHT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
    },
  },
  {
    title: 'Centered + Overhung Left',
    value: RotorTypeSelections.CenterLeft,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/CenterLeft',
    tolerancesImageSrc: './assets/img/tolerances/main/CenterLeft',
    index: 3,
    class: 'center-left',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.MIDLEFT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.MIDRIGHT_Px,
      },
    },
  },
  {
    title: 'Overhung Left',
    value: RotorTypeSelections.OverhungLeft,
    rotorSetupImageSrc: './assets/img/rotor-type-settings/OverhungLeft',
    tolerancesImageSrc: './assets/img/tolerances/main/OverhungLeft',
    index: 4,
    class: 'overhung-left',
    tolerancePlaneCoords: {
      [TolerancePlanePositions.Measuring]: {
        t1: IMAGE_RENDERER.MIDRIGHT_Px,
        t2: IMAGE_RENDERER.RIGHT_Px,
      },
      [TolerancePlanePositions.Correction]: {
        t1: IMAGE_RENDERER.LEFT_Px,
        t2: IMAGE_RENDERER.MIDLEFT_Px,
      },
    },
  },
];

export const rotorTypeConfigurationManager = {
  getImage: function (
    selectedCorrectionPlane?: number,
    mode?: LaserType,
    r1?: number,
    r2?: number,
    highlightField?: string,
    r1GreaterThanR2?: BehaviorSubject<boolean>
  ) {
    const rotorTypeSelection = getRotorTypeSelection(selectedCorrectionPlane);
    const imageBase = rotorTypeSelection.rotorSetupImageSrc;

    if (selectedCorrectionPlane === null || typeof selectedCorrectionPlane !== 'number') {
      return '';
    } else if (selectedCorrectionPlane === 1) {
      if (highlightField === 'r1' || highlightField === 'r2') {
        return `${imageBase}/${mode}/highlightR.png`;
      } else {
        return `${imageBase}/${mode}/no-highlight.png`;
      }
    }

    let size = 'R1EqualR2';
    const imageUrl = rotorTypeSelection.rotorSetupImageSrc;

    if (r1 > r2) {
      size = 'R1LargerR2';
      r1GreaterThanR2.next(true);
    } else if (r1 < r2) {
      size = 'R1SmallerR2';
      r1GreaterThanR2.next(false);
    } else {
      r1GreaterThanR2.next(null);
    }

    let highlightPart = 'no-highlight.png';

    if (highlightField === 'a') highlightPart = 'highlightA.png';
    else if (highlightField === 'b') highlightPart = 'highlightB.png';
    else if (highlightField === 'c') highlightPart = 'highlightC.png';
    else if (highlightField === 'r1') highlightPart = 'highlightR1.png';
    else if (highlightField === 'r2') highlightPart = 'highlightR2.png';

    return `${imageUrl}/${size}/${mode}/${highlightPart}`;
  },
};

export const ROTOR_TYPE_FORMS_STEPS: string[] = [
  'supportSettingsStep',
  'rotorTypeSettingsStep',
  'measurementSettingsStep',
  'correctionMethodStep',
  'tolerancesStep',
  'calibrationsAndCompensationsStep',
  'summaryStep',
];

export const FORM_STEPS_FOR_UPDATE: { name: string; prefix: string }[] = [
  { name: 'rotorTypeSettingsStep', prefix: null },
  { name: 'measurementSettingsStep', prefix: null },
  { name: 'correctionMethodStep', prefix: 'left' },
  { name: 'correctionMethodStep', prefix: 'right' },
  { name: 'correctionLocationsStep', prefix: 'left' },
  { name: 'correctionLocationsStep', prefix: 'right' },
  { name: 'tolerancesStep', prefix: 'userDefined' },
  { name: 'tolerancesStep', prefix: 'isoCalculation' },
  { name: 'calibratiosnAndCompensationsStep', prefix: 'calibrationAndCompensation' },
  { name: 'summaryStep', prefix: null },
];

export const WORKFLOW_ITEMS: WorkflowStep[] = [
  { title: 'Support Settings', valid: false },
  { title: 'Rotor Type Settings', valid: false },
  { title: 'Measurement Settings', valid: false },
  { title: 'Correction Method', valid: false },
  { title: 'Correction Locations', valid: false },
  { title: 'Tolerances', valid: false },
  { title: 'Calibrations and Compensations', valid: false },
  { title: 'Summary', valid: false },
];

export const EXCLUDED_CONTROLS = [
  'dynamicTolerance1',
  'dynamicTolerance2',
  'staticTolerance',
  'selectedUnbalanceUnit',
  'coupleTolerance',
];
export const INFO_MODAL_TEXT = {
  rotorTypeSpecificModalText:
    'The rotor type specific calibration is generally used to establish an assignment of the vibration measurement values to unbalances or to achieve a more accurate unbalance indication if permanent calibration is available.',
  biasCompensationModalText:
    'The single compensation shifts the measured unbalance with a defined vector. This displacement vector can be freely defined or determined by a measurement.',
  keyCompensationModalText:
    'The unbalance effect of a feather key is maintained mathematically. This means that the rest of a series of rotors can be balanced without having to insert a (half) key each time.',
  indexCompensationModalText:
    'This function makes it possible to compensate for the unbalance influence of an auxiliary part on the rotor unbalance.',
};

export const getRotorTypeSelection = (
  selectedCorrectionPlane: number
): RotorTypeConfigurationModel => {
  const arrayPlaneIndex = !!selectedCorrectionPlane ? selectedCorrectionPlane - 1 : 0;

  return RotorTypeConfiguration[arrayPlaneIndex];
};

import { SelectItemV2 } from '@schenck/rotec-components';
import { UnbalanceReadout } from 'src/utils/enums/rotor-type.enum';

export const UNBALANCE_ITEMS: SelectItemV2<UnbalanceReadout>[] = [
  { name: 'Dynamic', value: UnbalanceReadout.Dynamic },
  { name: 'Static / Dynamic', value: UnbalanceReadout.StaticDynamic },
  { name: 'Static / Couple', value: UnbalanceReadout.StaticCouple },
];

export const ISO_CALCULATION_CONTROLS = [
  'balancingQualityGrade',
  'centerGravity1',
  'centerGravity2',
  'centerGravity',
  'selectedUnbalanceUnit',
  'rotorMass',
  'nominalSpeed',
  'deviation',
];
